import { z } from 'zod';
import { ffuProgramSchema, ffuShapeSchema } from './ffu-output.model';
import { northMethodSchema } from './project-file-drawing.model';
import { specialStoreyKindSchema } from './storey-level.model';

/**
 * The buildings of a batch as the site model step read them from every plan
 * page at once: each storey's outline in metres, all in one frame (x east,
 * y south), so buildings stand where they stand and storeys stack as built.
 */
export const siteModelStoreySchema = z.object({
  /** 0 is the entrance storey. */
  index: z.number().int(),
  name: z.string().nullable(),
  planNumbers: z.array(z.number().int()),
  /**
   * The rest of the storey as the site model returned it, in final-output
   * terms. Older site models lack them.
   */
  kind: specialStoreyKindSchema.optional(),
  height: z.number().nullable().optional(),
  inner_height: z.number().nullable().optional(),
  gfa: z.number().nullable().optional(),
  elevation: z.number().nullable().optional(),
  elevationMarkM: z.number().nullable().optional(),
  activities: z.array(ffuProgramSchema).optional(),
  /** The drawing the outline was read from. Absent when none was named. */
  drawingId: z.string().optional(),
  /** Page the outline was read from. */
  source: z
    .object({
      fileId: z.string(),
      pageIndex: z.number().int(),
      /** Size of the page image the model saw. Older site models lack it. */
      widthPx: z.number().optional(),
      heightPx: z.number().optional(),
    })
    .nullable(),
  /**
   * Degrees clockwise from page up to the drawing's north. Older site models
   * lack it.
   */
  northDeg: z.number().optional(),
  /** How {@link northDeg} is known. Older site models lack it. */
  northMethod: northMethodSchema.optional(),
  /**
   * The page whose drawing {@link northDeg} was matched from, when
   * {@link northMethod} is `matched_drawing`. Absent otherwise.
   */
  northMatchedFrom: z
    .object({ fileId: z.string(), pageIndex: z.number().int() })
    .optional(),
  shapes: z.array(ffuShapeSchema),
  /**
   * Where each shape's corners sit on the source page, as fractions of its
   * width and height (0,0 top left). Read on the bottom storey only.
   */
  pageOutlines: z
    .array(
      z.object({
        shapeId: z.string(),
        points: z.array(z.tuple([z.number(), z.number()])),
      }),
    )
    .optional(),
  /**
   * Where each shape's corners sit on the site plan page, as fractions like
   * {@link pageOutlines}. Read on the bottom storey only.
   */
  siteOutlines: z
    .array(
      z.object({
        shapeId: z.string(),
        points: z.array(z.tuple([z.number(), z.number()])),
      }),
    )
    .optional(),
});
export type SiteModelStorey = z.infer<typeof siteModelStoreySchema>;

export const siteModelBuildingSchema = z.object({
  /** Building instance ids from the classification. */
  ids: z.array(z.string()),
  name: z.string().nullable(),
  storeys: z.array(siteModelStoreySchema),
});
export type SiteModelBuilding = z.infer<typeof siteModelBuildingSchema>;

export const siteModelSchema = z.object({
  buildings: z.array(siteModelBuildingSchema),
  /** The site plan page that set where the buildings stand. */
  sitePlan: z
    .object({
      fileId: z.string(),
      pageIndex: z.number().int(),
      widthPx: z.number(),
      heightPx: z.number(),
    })
    .optional(),
});
export type SiteModel = z.infer<typeof siteModelSchema>;
